import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSound } from '../contexts/SoundContext';
import { saveGameResult } from '../utils/gameResultsService';
import { log } from '../utils/logger';
import SoundToggle from './SoundToggle';

interface MathProblem {
  question: string;
  answer: number;
  options: number[];
}

const MentalMathSpeedRound = () => {
  const { isAuthenticated, user } = useAuth();
  const { playSound } = useSound();
  const [currentProblem, setCurrentProblem] = useState<MathProblem | null>(null);
  const [currentProblemIndex, setCurrentProblemIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60); // 60 seconds per round
  const [gameActive, setGameActive] = useState(false);
  const [gameComplete, setGameComplete] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<'correct' | 'incorrect' | null>(null);
  const [totalProblems, setTotalProblems] = useState(0);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const [startTime, setStartTime] = useState(0);

  // Generate random math problem
  const generateProblem = (): MathProblem => {
    const operations = ['+', '-', '*'];
    const operation = operations[Math.floor(Math.random() * operations.length)];
    
    let num1: number, num2: number, answer: number, question: string;
    
    switch (operation) {
      case '+':
        num1 = Math.floor(Math.random() * 50) + 10; // 10-59
        num2 = Math.floor(Math.random() * 50) + 10; // 10-59
        answer = num1 + num2;
        question = `${num1} + ${num2}`;
        break;
      case '-':
        num1 = Math.floor(Math.random() * 50) + 30; // 30-79
        num2 = Math.floor(Math.random() * 20) + 5;  // 5-24
        answer = num1 - num2;
        question = `${num1} - ${num2}`;
        break;
      case '*':
        num1 = Math.floor(Math.random() * 12) + 2; // 2-13
        num2 = Math.floor(Math.random() * 12) + 2; // 2-13
        answer = num1 * num2;
        question = `${num1} × ${num2}`;
        break;
      default:
        num1 = 2; num2 = 3; answer = 5; question = '2 + 3';
    }

    // Generate 3 wrong answers
    const wrongAnswers: number[] = [];
    while (wrongAnswers.length < 3) {
      const offset = Math.floor(Math.random() * 20) - 10; // -10 to +10
      const wrongAnswer = answer + offset;
      if (wrongAnswer !== answer && wrongAnswer > 0 && !wrongAnswers.includes(wrongAnswer)) {
        wrongAnswers.push(wrongAnswer);
      }
    }

    // Shuffle options
    const options = [answer, ...wrongAnswers].sort(() => Math.random() - 0.5);
    
    return { question, answer, options };
  };

  // Start game
  const startGame = () => {
    setGameActive(true);
    setGameComplete(false);
    setScore(0);
    setTimeLeft(60);
    setCurrentProblemIndex(0);
    setTotalProblems(0);
    setSelectedAnswer(null);
    setFeedback(null);
    setStartTime(Date.now());
    setCurrentProblem(generateProblem());
  };

  // Handle answer selection
  const handleAnswerSelect = (selectedAnswer: number) => {
    if (!currentProblem || !gameActive) return;

    setSelectedAnswer(selectedAnswer);
    const isCorrect = selectedAnswer === currentProblem.answer;
    
    if (isCorrect) {
      setScore(prev => prev + 1);
      setFeedback('correct');
      playSound('correct');
    } else {
      setFeedback('incorrect');
      playSound('incorrect');
    }

    setTotalProblems(prev => prev + 1);

    // Move to next problem after brief feedback
    setTimeout(() => {
      setFeedback(null);
      setSelectedAnswer(null);
      setCurrentProblem(generateProblem());
      setCurrentProblemIndex(prev => prev + 1);
    }, 1000);
  };

  // Timer effect
  useEffect(() => {
    if (!gameActive || gameComplete) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          endGame();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [gameActive, gameComplete]);

  // End game
  const endGame = async () => {
    setGameActive(false);
    setGameComplete(true);
    playSound('results');
    
    const completionTime = Date.now() - startTime;
    const accuracy = totalProblems > 0 ? (score / totalProblems) * 100 : 0;

    log(`Mental Math completed: ${score}/${totalProblems} in ${completionTime}ms`);

    if (isAuthenticated && user) {
      try {
        await saveGameResult({
          user_id: user.id,
          game_type: 'mental_math_speed',
          score,
          completion_time_seconds: Math.round(completionTime / 1000), // Convert ms to seconds
          hints_used: 0,
          accuracy: Math.round(accuracy),
          completed: true
        });
      } catch (error) {
        console.error('Failed to save game result:', error);
      }
    } else {
      setShowSignupPrompt(true);
    }
  };

  // Reset for new game
  const resetGame = () => {
    setGameActive(false);
    setGameComplete(false);
    setCurrentProblem(null);
    setScore(0);
    setTimeLeft(60);
    setSelectedAnswer(null);
    setFeedback(null);
    setTotalProblems(0);
    setCurrentProblemIndex(0);
    setShowSignupPrompt(false);
  };

  const accuracy = totalProblems > 0 ? Math.round((score / totalProblems) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 dark:from-gray-900 dark:to-gray-800 py-4 sm:py-6 lg:py-8">
      <div className="max-w-2xl mx-auto px-3 sm:px-4">
        {/* Navigation Header */}
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <Link to="/" className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-semibold text-sm sm:text-base">
            ← Back to Games
          </Link>
          <SoundToggle />
        </div>

        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 dark:text-gray-100 mb-2">
            Mental Math Speed Round
          </h1>
          <p className="text-sm sm:text-base text-gray-600 px-2 sm:px-0">
            Solve as many problems as you can in 60 seconds!
          </p>
        </div>

        {/* Game Stats Bar */}
        {gameActive && (
          <div className="bg-white rounded-lg shadow-md p-3 sm:p-4 mb-4 sm:mb-6">
            <div className="flex justify-between items-center text-sm sm:text-base lg:text-lg">
              <div className="font-semibold text-gray-700">
                Score: <span className="text-purple-600">{score}</span>
              </div>
              <div className="font-semibold text-gray-700">
                Time: <span className={`${timeLeft <= 10 ? 'text-red-500' : 'text-green-600'}`}>
                  {timeLeft}s
                </span>
              </div>
              <div className="font-semibold text-gray-700">
                Problem: <span className="text-blue-600">{currentProblemIndex + 1}</span>
              </div>
            </div>
          </div>
        )}

        {/* Game Area */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 lg:p-8 mb-4 sm:mb-6">
          {!gameActive && !gameComplete && (
            <div className="text-center">
              <div className="text-4xl sm:text-5xl lg:text-6xl mb-4 sm:mb-6">🧠</div>
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">
                Ready to test your mental math skills?
              </h2>
              <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8 px-2 sm:px-0">
                You'll have 60 seconds to solve as many addition, subtraction, and multiplication problems as possible. Choose the correct answer from the four options provided.
              </p>
              <button
                onClick={startGame}
                className="bg-purple-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-lg sm:text-xl font-semibold hover:bg-purple-700 transition-colors shadow-lg"
              >
                Start Speed Round
              </button>
            </div>
          )}

          {gameActive && currentProblem && (
            <div className="text-center">
              {/* Problem */}
              <div className="mb-6 sm:mb-8">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 dark:text-gray-100 mb-4 sm:mb-6">
                  {currentProblem.question} = ?
                </h2>
                
                {/* Answer Options */}
                <div className="grid grid-cols-2 gap-3 sm:gap-4 max-w-xs sm:max-w-md mx-auto">
                  {currentProblem.options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleAnswerSelect(option)}
                      disabled={selectedAnswer !== null}
                      className={`
                        p-3 sm:p-4 text-lg sm:text-xl font-semibold rounded-lg border-2 transition-all
                        ${selectedAnswer === option
                          ? feedback === 'correct'
                            ? 'bg-green-100 border-green-500 text-green-800'
                            : 'bg-red-100 border-red-500 text-red-800'
                          : selectedAnswer !== null && option === currentProblem.answer
                            ? 'bg-green-100 border-green-500 text-green-800'
                            : 'bg-gray-50 border-gray-300 hover:bg-gray-100 hover:border-gray-400'
                        }
                        ${selectedAnswer !== null ? 'cursor-not-allowed' : 'cursor-pointer'}
                      `}
                    >
                      {option}
                    </button>
                  ))}
                </div>

                {/* Feedback */}
                {feedback && (
                  <div className={`mt-4 text-lg font-semibold ${
                    feedback === 'correct' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {feedback === 'correct' ? '✅ Correct!' : '❌ Incorrect'}
                  </div>
                )}
              </div>
            </div>
          )}

          {gameComplete && (
            <div className="text-center">
              <div className="text-6xl mb-6">🎯</div>
              <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-4">
                Speed Round Complete!
              </h2>
              
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-3xl font-bold text-purple-600">{score}</div>
                    <div className="text-sm text-gray-600">Correct Answers</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-blue-600">{totalProblems}</div>
                    <div className="text-sm text-gray-600">Total Problems</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-green-600">{accuracy}%</div>
                    <div className="text-sm text-gray-600">Accuracy</div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <button
                  onClick={startGame}
                  className="bg-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors mr-4"
                >
                  Play Again
                </button>
                <Link
                  to="/games"
                  className="bg-gray-300 text-gray-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-400 transition-colors inline-block"
                >
                  Back to Games
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Signup Prompt */}
        {showSignupPrompt && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-blue-800 mb-2">
                Great job! Want to track your progress?
              </h3>
              <p className="text-blue-700 mb-4">
                Sign up to save your scores and track your progress!
              </p>
              <Link
                to="/login"
                className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Sign Up / Login
              </Link>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">How to Play</h3>
          <ul className="text-gray-600 space-y-2">
            <li>• You have 60 seconds to solve as many problems as possible</li>
            <li>• Problems include addition, subtraction, and multiplication</li>
            <li>• Click the correct answer from the four options</li>
            <li>• Your score and accuracy will be tracked</li>
            <li>• Sign up to save your results and compete with others!</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MentalMathSpeedRound;